import { useAuthenticationStore } from '~/auth/useAuthenticationStore';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { usePermissions } from '~/hooks/usePermissions';
import { checkIsAdmin } from '~/permissions/checkIsAdmin';
import { checkIsSuperAdmin } from '~/permissions/checkIsSuperAdmin';
import { isHomeOn } from '~/permissions/hasHome';
import { api } from '~/redux/api/createApi';
import { RbacPermission } from '~/types/Rbac';

import { meetsHomePrereqs } from '../utils/meetsHomePrereqs';

const HOME_PERMISSIONS: RbacPermission[] = [
  { resourceId: 'documents', resourceType: 'view' },
  { resourceId: 'conversational_ai', resourceType: 'edit' },
];

interface Options {
  // Only callers that read `canManageHomeSettings` need `can_edit` while
  // `newhome-ekp` is on; everyone else must not wait on Pilot.
  withManage?: boolean;
}

export function useHomeAccess({ withManage = false }: Options = {}) {
  const user = useCurrentUser();
  const { isLoggedIn } = useAuthenticationStore();
  // Read directly: `newhome-ekp` on short-circuits the company query.
  const hasNewHome = useFlag(FlagType.Home);
  const adminDate = useFlag(FlagType.HomeAdminSettings);
  const hasAdminDate = Boolean(adminDate);
  const hasHome = isHomeOn(hasNewHome, adminDate);

  // `checkIsAdmin` is broader than Pilot's `FullAdminPermission` (it lets
  // custom admins and super admins through), so it only skips the query for
  // people who cannot pass; `can_edit` decides.
  const skip = !hasHome || !checkIsAdmin(user) || (hasNewHome && !withManage);

  const { data, isError, isLoading } =
    api.endpoints.getCompanyHomeSettings.useQuery(undefined, { skip });

  const canEdit = Boolean(!skip && !isLoading && !isError && data?.canEdit);

  const isSuperAdmin = checkIsSuperAdmin(user);
  const { checkPermission, isLoading: isPermissionsLoading } = usePermissions({
    permissions: HOME_PERMISSIONS,
    skip: !isLoggedIn,
  });
  const isWaitingOnPermissions =
    hasHome && !isSuperAdmin && isPermissionsLoading;
  const hasPrereqs =
    !isWaitingOnPermissions &&
    meetsHomePrereqs({ isSuperAdmin, checkPermission });

  return {
    canUseHome: hasPrereqs && (hasNewHome || (hasAdminDate && canEdit)),
    canManageHomeSettings: hasHome && canEdit,
    isLoading: (!skip && isLoading) || isWaitingOnPermissions,
  };
}
